import { fabFacebookF } from 'shared/@generated/icons/fabFacebookF';
import { fabInstagram } from 'shared/@generated/icons/fabInstagram';
import { fabLinkedinIn } from 'shared/@generated/icons/fabLinkedinIn';
import { fabTiktok } from 'shared/@generated/icons/fabTiktok';
import { Fragment } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Link } from 'shared/components/link';
import { openCookieSettings } from 'shared/library/cookie-consent';
import { Text } from 'shared/components/text';
import { Props } from 'shared/@types';
import { PaymentBrands } from 'shared/components/payment-brands';
import { SvgImage } from 'shared/components/svg-image';
import { LocaleSwitcher } from 'shared/components/locale-switcher';
import { formatAppVersion } from 'shared/library/app-version';
import { Icon } from 'shared/components/icon';
import { useRoute } from 'shared/library/routes/route';
import { useRouter } from 'shared/library/use-router';
import { LanguageCode } from 'shared/library/i18n/languages';
import { LangMap } from 'shared/library/routes/define-route';
import logo from 'shared/assets/logo.svg';

type LocalizedFooterText = Partial<Record<LanguageCode, string>> & { en: string };

type FooterSection = {
  title: string;
  links: {
    href: string;
    label: string;
  }[];
};

const POPULAR_DOG_BREEDS: { label: LocalizedFooterText; slug: LangMap }[] = [
  {
    label: {
      da: 'Labrador retriever',
      en: 'Labrador retriever',
      fi: 'Labradorinnoutaja',
      no: 'Labrador retriever',
      sv: 'Labrador retriever',
    },
    slug: {
      en: 'labrador-retriever',
      no: 'labrador-retriever',
      sv: 'labrador-retriever',
    },
  },
  {
    label: {
      da: 'Fransk bulldog',
      en: 'French bulldog',
      fi: 'Ranskanbulldoggi',
      no: 'Fransk bulldog',
      sv: 'Fransk bulldogg',
    },
    slug: {
      da: 'fransk-bulldog',
      en: 'french-bulldog',
      no: 'fransk-bulldog',
      sv: 'fransk-bulldogg',
    },
  },
  {
    label: {
      da: 'Schæferhund',
      en: 'German shepherd dog',
      fi: 'Saksanpaimenkoira',
      no: 'Schäferhund',
      sv: 'Tysk schäferhund',
    },
    slug: {
      da: 'schaeferhund',
      en: 'german-shepherd-dog',
      no: 'schaferhund',
      sv: 'tysk-schaferhund',
    },
  },
  {
    label: {
      da: 'Jämthund',
      en: 'Jämthund',
      fi: 'Jämtlanninpystykorva',
      no: 'Jämthund',
      sv: 'Jämthund',
    },
    slug: {
      en: 'jamthund',
      no: 'jamthund',
      sv: 'jamthund',
    },
  },
  {
    label: {
      da: 'Cocker spaniel',
      en: 'Cocker spaniel',
      fi: 'Cockerspanieli',
      no: 'Cocker spaniel',
      sv: 'Cocker spaniel',
    },
    slug: {
      en: 'cocker-spaniel',
      no: 'cocker-spaniel',
      sv: 'cocker-spaniel',
    },
  },
  {
    label: {
      da: 'Bichon havanais',
      en: 'Havanese',
      fi: 'Havannankoira',
      no: 'Bichon havanais',
      sv: 'Bichon havanais',
    },
    slug: {
      da: 'bichon-havanais',
      en: 'havanese',
      no: 'bichon-havanais',
      sv: 'bichon-havanais',
    },
  },
  {
    label: {
      da: 'Staffordshire bullterrier',
      en: 'Staffordshire bull terrier',
      fi: 'Staffordshirenbullterrieri',
      no: 'Staffordshire bullterrier',
      sv: 'Staffordshire bullterrier',
    },
    slug: {
      en: 'staffordshire-bull-terrier',
      no: 'staffordshire-bull-terrier',
      sv: 'staffordshire-bullterrier',
    },
  },
  {
    label: {
      da: 'Border collie',
      en: 'Border collie',
      fi: 'Bordercollie',
      no: 'Border collie',
      sv: 'Border collie',
    },
    slug: {
      en: 'border-collie',
      no: 'border-collie',
      sv: 'border-collie',
    },
  },
  {
    label: {
      da: 'Jack russell terrier',
      en: 'Jack russell terrier',
      fi: 'Jackrussellinterrieri',
      no: 'Jack russell terrier',
      sv: 'Jack russell terrier',
    },
    slug: {
      en: 'jack-russell-terrier',
      no: 'jack-russell-terrier',
      sv: 'jack-russell-terrier',
    },
  },
  {
    label: {
      da: 'Australian shepherd',
      en: 'Australian shepherd',
      fi: 'Australianpaimenkoira',
      no: 'Australian shepherd',
      sv: 'Australian shepherd',
    },
    slug: {
      en: 'australian-shepherd',
      no: 'australian-shepherd',
      sv: 'australian-shepherd',
    },
  },
  {
    label: {
      da: 'Chihuahua',
      en: 'Chihuahua',
      fi: 'Chihuahua',
      no: 'Chihuahua',
      sv: 'Chihuahua',
    },
    slug: {
      en: 'chihuahua',
      no: 'chihuahua',
      sv: 'chihuahua',
    },
  },
  {
    label: {
      da: 'Dachshund',
      en: 'Dachshund',
      fi: 'Mäyräkoira',
      no: 'Dachshund',
      sv: 'Tax',
    },
    slug: {
      da: 'tax',
      en: 'dachshund',
      no: 'dachshund',
      sv: 'tax',
    },
  },
  {
    label: {
      da: 'Cavalier king charles spaniel',
      en: 'Cavalier king charles spaniel',
      fi: 'Cavalier kingcharlesinspanieli',
      no: 'Cavalier king charles spaniel',
      sv: 'Cavalier king charles spaniel',
    },
    slug: {
      en: 'cavalier-king-charles-spaniel',
      no: 'cavalier-king-charles-spaniel',
      sv: 'cavalier-king-charles-spaniel',
    },
  },
  {
    label: {
      da: 'Shetland sheepdog',
      en: 'Shetland sheepdog',
      fi: 'Shetlanninlammaskoira',
      no: 'Shetland sheepdog',
      sv: 'Shetland sheepdog',
    },
    slug: {
      en: 'shetland-sheepdog',
      no: 'shetland-sheepdog',
      sv: 'shetland-sheepdog',
    },
  },
  {
    label: {
      da: 'Pomeranian',
      en: 'Pomeranian',
      fi: 'Pomeranian',
      no: 'Pomeranian',
      sv: 'Pomeranian',
    },
    slug: {
      en: 'pomeranian',
      no: 'pomeranian',
      sv: 'pomeranian',
    },
  },
  {
    label: {
      da: 'Dværgschnauzer',
      en: 'Miniature schnauzer',
      fi: 'Kääpiösnautseri',
      no: 'Dvergschnauzer',
      sv: 'Dvärgschnauzer',
    },
    slug: {
      da: 'dvaergschnauzer',
      en: 'miniature-schnauzer',
      no: 'dvergschnauzer',
      sv: 'dvargschnauzer',
    },
  },
  {
    label: {
      da: 'Whippet',
      en: 'Whippet',
      fi: 'Whippet',
      no: 'Whippet',
      sv: 'Whippet',
    },
    slug: {
      en: 'whippet',
      no: 'whippet',
      sv: 'whippet',
    },
  },
  {
    label: {
      da: 'Mops',
      en: 'Pug',
      fi: 'Mopsi',
      no: 'Mops',
      sv: 'Mops',
    },
    slug: {
      da: 'mops',
      en: 'pug',
      no: 'mops',
      sv: 'mops',
    },
  },
];

const POPULAR_CAT_BREEDS: { label: LocalizedFooterText; slug: LangMap }[] = [
  {
    label: {
      da: 'Maine Coon',
      en: 'Maine Coon',
      fi: 'Maine coon',
      no: 'Maine Coon',
      sv: 'Maine coon',
    },
    slug: {
      en: 'maine-coon',
      no: 'maine-coon',
      sv: 'maine-coon',
    },
  },
  {
    label: {
      da: 'Ragdoll',
      en: 'Ragdoll',
      fi: 'Ragdoll',
      no: 'Ragdoll',
      sv: 'Ragdoll',
    },
    slug: {
      en: 'ragdoll',
      no: 'ragdoll',
      sv: 'ragdoll',
    },
  },
  {
    label: {
      da: 'Sibirisk kat',
      en: 'Siberian cat',
      fi: 'Siperiankissa',
      no: 'Sibirsk skogkatt',
      sv: 'Sibirisk katt',
    },
    slug: {
      da: 'sibirisk-kat',
      en: 'siberian-forest',
      no: 'sibirsk-skogkatt',
      sv: 'sibirisk-katt',
    },
  },
  {
    label: {
      da: 'Britisk korthår',
      en: 'British shorthair',
      fi: 'Brittiläinen lyhytkarva',
      no: 'Britisk korthår',
      sv: 'Brittiskt korthår',
    },
    slug: {
      da: 'britisk-korthar',
      en: 'british-shorthair',
      no: 'britisk-korthar',
      sv: 'brittiskt-korthar',
    },
  },
  {
    label: {
      da: 'Norsk skovkat',
      en: 'Norwegian forest cat',
      fi: 'Norjalainen metsäkissa',
      no: 'Norsk skogkatt',
      sv: 'Norsk skogkatt',
    },
    slug: {
      da: 'norsk-skovkat',
      en: 'norwegian-forest',
      no: 'norsk-skogkatt',
      sv: 'norsk-skogkatt',
    },
  },
  {
    label: {
      da: 'Devon Rex',
      en: 'Devon Rex',
      fi: 'Devon rex',
      no: 'Devon Rex',
      sv: 'Devon rex',
    },
    slug: {
      en: 'devon-rex',
      no: 'devon-rex',
      sv: 'devon-rex',
    },
  },
  {
    label: {
      da: 'Bengal',
      en: 'Bengal',
      fi: 'Bengali',
      no: 'Bengal',
      sv: 'Bengal',
    },
    slug: {
      en: 'bengal',
      no: 'bengal',
      sv: 'bengal',
    },
  },
  {
    label: {
      da: 'Hellig birma',
      en: 'Birman',
      fi: 'Pyhä birma',
      no: 'Hellig birma',
      sv: 'Helig birma',
    },
    slug: {
      da: 'hellig-birma',
      en: 'birman',
      no: 'helig-birma',
      sv: 'helig-birma',
    },
  },
  {
    label: {
      da: 'Sphynx',
      en: 'Sphynx',
      fi: 'Sfinksi',
      no: 'Sphynx',
      sv: 'Sphynx',
    },
    slug: {
      en: 'sphynx',
      no: 'sphynx',
      sv: 'sphynx',
    },
  },
  {
    label: {
      da: 'Perser',
      en: 'Persian',
      fi: 'Persialainen',
      no: 'Perser',
      sv: 'Perser',
    },
    slug: {
      da: 'perser',
      en: 'persian',
      no: 'perser',
      sv: 'perser',
    },
  },
  {
    label: {
      da: 'Burma',
      en: 'Burmese',
      fi: 'Burma',
      no: 'Burma',
      sv: 'Burma',
    },
    slug: {
      da: 'burma-sacred-birman',
      en: 'burmese',
      no: 'burma',
      sv: 'burma',
    },
  },
  {
    label: {
      da: 'Orientalsk korthår',
      en: 'Oriental shorthair',
      fi: 'Orientaalinen lyhytkarva',
      no: 'Orientalsk korthår',
      sv: 'Orientaliskt korthår',
    },
    slug: {
      da: 'orientalsk-korthar',
      en: 'oriental-short-hair',
      no: 'orientalsk-korthar',
      sv: 'orientaliskt-korthar',
    },
  },
  {
    label: {
      da: 'Cornish Rex',
      en: 'Cornish Rex',
      fi: 'Cornish rex',
      no: 'Cornish Rex',
      sv: 'Cornish rex',
    },
    slug: {
      en: 'cornish-rex',
      no: 'cornish-rex',
      sv: 'cornish-rex',
    },
  },
  {
    label: {
      da: 'Abessinier',
      en: 'Abyssinian',
      fi: 'Abessinialainen',
      no: 'Abyssiner',
      sv: 'Abessinier',
    },
    slug: {
      da: 'abyssinier',
      en: 'abyssinian',
      no: 'abyssiner-katt',
      sv: 'abessinier',
    },
  },
  {
    label: {
      da: 'Ragamuffin',
      en: 'Ragamuffin',
      fi: 'Ragamuffin',
      no: 'Ragamuffin',
      sv: 'Ragamuffin',
    },
    slug: {
      en: 'ragamuffin',
      no: 'ragamuffin',
      sv: 'ragamuffin',
    },
  },
  {
    label: {
      da: 'Tonkines',
      en: 'Tonkinese',
      fi: 'Tonkineesi',
      no: 'Tonkines',
      sv: 'Tonkines',
    },
    slug: {
      da: 'tonkineser',
      en: 'tonkinese',
      no: 'tonkines',
      sv: 'tonkines',
    },
  },
  {
    label: {
      da: 'Russian Blue',
      en: 'Russian Blue',
      fi: 'Venäjänsininen',
      no: 'Russian Blue',
      sv: 'Russian blue',
    },
    slug: {
      en: 'russian-blue',
      no: 'russian-blue',
      sv: 'russian-blue',
    },
  },
];

const BREEDER_CITIES = [
  { name: 'Stockholm', slug: 'stockholm' },
  { name: 'Göteborg', slug: 'gothenburg' },
  { name: 'Malmö', slug: 'malmo' },
  { name: 'Uppsala', slug: 'uppsala' },
  { name: 'Linköping', slug: 'linkoping' },
  { name: 'Örebro', slug: 'orebro' },
  { name: 'Umeå', slug: 'umea' },
  { name: 'Västerås', slug: 'vasteras' },
  { name: 'Jönköping', slug: 'jonkoping' },
  { name: 'Helsingborg', slug: 'helsingborg' },
];

const SHOP_CATEGORIES: { label: LocalizedFooterText; slug: LangMap }[] = [
  {
    label: { da: 'Hvalp', en: 'Puppy', fi: 'Pentu', no: 'Valpe', sv: 'Valp' },
    slug: { da: 'hvalp', en: 'puppy', no: 'valpe', sv: 'valp' },
  },
  {
    label: { da: 'Godbidder', en: 'Treats', fi: 'Herkut', no: 'Godteri', sv: 'Godis' },
    slug: { da: 'godbidder', en: 'treats', no: 'godteri', sv: 'godis' },
  },
  {
    label: { da: 'Hundemad', en: 'Dog food', fi: 'Koiranruoka', no: 'Hundfôr', sv: 'Hundfoder' },
    slug: { da: 'hundemad', en: 'dog-food', no: 'hundfoder', sv: 'hundfoder' },
  },
  {
    label: { da: 'Legetøj', en: 'Toys', fi: 'Lelut', no: 'Leker', sv: 'Leksaker' },
    slug: { da: 'legetøj', en: 'toys', no: 'leker', sv: 'leksaker' },
  },
  {
    label: { da: 'Gåtur', en: 'Walk', fi: 'Ulkoilu', no: 'Promenade', sv: 'Promenad' },
    slug: { da: 'walk', en: 'walk', no: 'promenade', sv: 'promenad' },
  },
  {
    label: {
      da: 'Foderplads',
      en: 'Feeding area',
      fi: 'Ruokailu',
      no: 'Spiseplass',
      sv: 'Matplats',
    },
    slug: { da: 'fodringsområde', en: 'feeding-area', no: 'spiseplass', sv: 'matplats' },
  },
  {
    label: { da: 'Pleje', en: 'Care', fi: 'Hoito', no: 'Stell', sv: 'Skötsel' },
    slug: { da: 'omsorg', en: 'caring', no: 'stell', sv: 'skotsel' },
  },
  {
    label: {
      da: 'Soveplads',
      en: 'Sleeping place',
      fi: 'Nukkumapaikka',
      no: 'Soveplass',
      sv: 'Sovplats',
    },
    slug: { da: 'soveplads', en: 'sleeping-place', no: 'soveplass', sv: 'sovplats' },
  },
];

function resolveLocalizedFooterText(text: LocalizedFooterText, languageCode: LanguageCode) {
  return text[languageCode] ?? text.en ?? text.sv ?? Object.values(text)[0] ?? '';
}

const Footer = () => {
  const { t } = useTranslation('web');
  const route = useRoute();
  const { languageCode } = useRouter();

  const legalLinks = [
    {
      href: route('policies.purchase-terms'),
      label: t('layout.footer.purchase_terms'),
    },
    {
      href: route('policies.terms'),
      label: t('layout.footer.terms_of_use'),
    },
    {
      href: route('policies.privacy'),
      label: t('layout.footer.privacy_policy'),
    },
    {
      href: route('policies.cookies'),
      label: t('layout.footer.cookie_policy'),
    },
  ];

  const breederDirectoryLinks = [
    {
      href: route('breeders.index'),
      label: t('layout.footer.sections.breeders.all_breeders'),
    },
    {
      href: route('breeders.dogs'),
      label: t('layout.footer.sections.breeders.dog_breeders'),
    },
    {
      href: route('breeders.cats'),
      label: t('layout.footer.sections.breeders.cat_breeders'),
    },
  ];

  const sections: FooterSection[] = [
    {
      title: t('layout.footer.sections.dogs.title'),
      links: [
        {
          href: route('buying-a-dog'),
          label: t('layout.footer.sections.dogs.buying_a_dog'),
        },
        {
          href: route('listings.dogs'),
          label: t('layout.footer.sections.dogs.dogs_for_sale'),
        },
        {
          href: route('listings.puppies'),
          label: t('layout.footer.sections.dogs.puppies_for_sale'),
        },
        {
          href: route('breeds.dogs'),
          label: t('layout.footer.sections.dogs.dog_breeds'),
        },
        {
          href: route('breeds.dogs.small'),
          label: t('layout.footer.sections.dogs.small_dog_breeds'),
        },
        {
          href: route('breeds.dogs.medium'),
          label: t('layout.footer.sections.dogs.medium_dog_breeds'),
        },
        {
          href: route('breeds.dogs.large'),
          label: t('layout.footer.sections.dogs.large_dog_breeds'),
        },
      ],
    },
    {
      title: t('layout.footer.sections.cats.title'),
      links: [
        {
          href: route('buying-a-cat'),
          label: t('layout.footer.sections.cats.buying_a_cat'),
        },
        {
          href: route('listings.cats'),
          label: t('layout.footer.sections.cats.cats_for_sale'),
        },
        {
          href: route('listings.kittens'),
          label: t('layout.footer.sections.cats.kittens_for_sale'),
        },
        {
          href: route('breeds.cats'),
          label: t('layout.footer.sections.cats.cat_breeds'),
        },
      ],
    },
    {
      title: t('layout.footer.sections.for_buyers.title'),
      links: [
        {
          href: route('buying-a-pet'),
          label: t('layout.footer.sections.for_buyers.buying_a_pet'),
        },
        {
          href: route('petpay.for-buyers'),
          label: t('layout.footer.sections.for_buyers.buy_with_petpay'),
        },
        {
          href: route('pet-insurance'),
          label: t('layout.footer.sections.for_buyers.pet_insurance'),
        },
      ],
    },
    {
      title: t('layout.footer.sections.tools.title'),
      links: [
        {
          href: route('breeds.dogs.advisor'),
          label: t('layout.footer.sections.tools.dog_breed_advisor'),
        },
        {
          href: route('pet-names.dog'),
          label: t('layout.footer.sections.tools.dog_names'),
        },
        {
          href: route('pet-names.cat'),
          label: t('layout.footer.sections.tools.cat_names'),
        },
        {
          href: route('pet-costs.dog'),
          label: t('layout.footer.sections.tools.dog_costs'),
        },
        {
          href: route('pet-costs.cat'),
          label: t('layout.footer.sections.tools.cat_costs'),
        },
        {
          href: route('dog-years'),
          label: t('layout.footer.sections.tools.dog_years'),
        },
      ],
    },
    {
      title: t('layout.footer.sections.breeders.title'),
      links: [
        {
          href: route('selling-a-dog'),
          label: t('layout.footer.sections.breeders.selling_a_dog'),
        },
        {
          href: route('selling-a-cat'),
          label: t('layout.footer.sections.breeders.selling_a_cat'),
        },
        {
          href: route('breeder-tools'),
          label: t('layout.footer.sections.breeders.breeder_tools'),
        },
        {
          href: route('petpay.for-sellers'),
          label: t('layout.footer.sections.breeders.sell_with_petpay'),
        },
        {
          href: route('litter-insurance'),
          label: t('layout.footer.sections.breeders.litter_insurance'),
        },
      ],
    },
    {
      title: t('layout.footer.sections.get_a_pet.title'),
      links: [
        {
          href: route('help.index'),
          label: t('layout.footer.sections.get_a_pet.help'),
        },
        {
          href: route('about'),
          label: t('layout.footer.sections.get_a_pet.about_us'),
        },
        {
          href: route('testimonials'),
          label: t('layout.footer.sections.get_a_pet.testimonials'),
        },
        {
          href: route('blog.index'),
          label: t('layout.footer.sections.get_a_pet.blog'),
        },
      ],
    },
    {
      title: t('layout.footer.sections.shop.title'),
      links: [
        {
          href: route('shop.home'),
          label: t('layout.footer.sections.shop.all_products'),
        },
        ...SHOP_CATEGORIES.map((category) => ({
          href: route('shop.category', { category: category.slug }),
          label: resolveLocalizedFooterText(category.label, languageCode),
        })),
      ],
    },
    {
      title: t('layout.footer.sections.popular_dog_breeds_title'),
      links: POPULAR_DOG_BREEDS.map((breed) => ({
        href: route('breeds.show', { breed: breed.slug }),
        label: resolveLocalizedFooterText(breed.label, languageCode),
      })),
    },
    {
      title: t('layout.footer.sections.popular_cat_breeds_title'),
      links: POPULAR_CAT_BREEDS.map((breed) => ({
        href: route('breeds.show', { breed: breed.slug }),
        label: resolveLocalizedFooterText(breed.label, languageCode),
      })),
    },
    {
      title: t('layout.footer.sections.breeder_cities_title'),
      links: [
        ...breederDirectoryLinks,
        ...BREEDER_CITIES.map((city) => ({
          href: route('breeders.by-city', { city: city.slug }),
          label: t('layout.footer.sections.breeder_in_city', { city: city.name }),
        })),
      ],
    },
  ];

  const footerRows = [
    [sections[2]!, sections[4]!, sections[3]!],
    [sections[0]!, sections[1]!, sections[5]!],
  ];
  const discoverySections = [sections[6]!, sections[7]!, sections[8]!, sections[9]!];

  return (
    <View
      accessibilityRole={'contentinfo' as any}
      className={'border-brand-brown-dark/10 border-t bg-white p-3 pb-16 md:p-10'}
    >
      <View className={'mx-auto w-full max-w-screen-xl p-3'}>
        <View className={'lg:flex-row lg:gap-16'}>
          <View className="lg:w-2/5">
            <View className="items-start">
              <SvgImage
                src={logo}
                alt={'Get a Pet Logo'}
                containerClassName={'aspect-logo h-12 md:h-16'}
              />
            </View>

            <View className={'mt-4 max-w-screen-sm'}>
              <Text className={'text-brand-brown text-pretty'}>
                {t('layout.footer.description')}
              </Text>
            </View>

            <View className={'mt-6 gap-6 md:flex-row md:items-center lg:flex-col lg:items-start'}>
              <PaymentBrands className={'justify-start'} />

              <View className={'flex-row justify-start gap-1.5'}>
                <Link href={'https://instagram.com/getapet_com'} openInNewTab={true} asBlock={true}>
                  <View
                    className={
                      'bg-brand-brown-dark items-center justify-center rounded-full p-2 group-hover:opacity-75'
                    }
                  >
                    <Icon icon={fabInstagram} className={'size-4 text-white'} />
                  </View>
                </Link>

                <Link href={'https://facebook.com/getapetcom'} openInNewTab={true} asBlock={true}>
                  <View
                    className={
                      'bg-brand-brown-dark items-center justify-center rounded-full p-2 group-hover:opacity-75'
                    }
                  >
                    <Icon icon={fabFacebookF} className={'size-4 text-white'} />
                  </View>
                </Link>

                <Link
                  href={'https://linkedin.com/company/getapetcom'}
                  openInNewTab={true}
                  asBlock={true}
                >
                  <View
                    className={
                      'bg-brand-brown-dark items-center justify-center rounded-full p-2 group-hover:opacity-75'
                    }
                  >
                    <Icon icon={fabLinkedinIn} className={'size-4 text-white'} />
                  </View>
                </Link>

                <Link href={'https://tiktok.com/@getapet_com'} openInNewTab={true} asBlock={true}>
                  <View
                    className={
                      'bg-brand-brown-dark items-center justify-center rounded-full p-2 group-hover:opacity-75'
                    }
                  >
                    <Icon icon={fabTiktok} className={'size-4 text-white'} />
                  </View>
                </Link>
              </View>
            </View>
          </View>

          <View className={'mt-12 lg:mt-0 lg:flex-1'}>
            <View className={'gap-8 lg:gap-12'}>
              {footerRows.map((row, rowIndex) => (
                <View
                  key={rowIndex}
                  className={'flex-cols-1 lg:flex-cols-3 flex-row gap-8 lg:gap-12'}
                >
                  {row.map((section) => (
                    <View key={section.title} className={'min-w-0'}>
                      <FooterHeading>{section.title}</FooterHeading>

                      <View accessibilityRole={'menu'} className="mt-3 space-y-2.5">
                        {section.links.map((link, linkIndex) => (
                          <FooterLink
                            key={`${section.title}-${link.href}-${linkIndex}`}
                            href={link.href}
                          >
                            {link.label}
                          </FooterLink>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </View>
        </View>

        <View
          className={
            'border-brand-brown-dark/10 flex-cols-1 md:flex-cols-2 lg:flex-cols-4 mt-12 flex-row gap-8 border-t pt-12 lg:gap-12'
          }
        >
          {discoverySections.map((section) => (
            <View key={section.title} className={'min-w-0'}>
              <FooterHeading>{section.title}</FooterHeading>

              <View accessibilityRole={'menu'} className="mt-3 space-y-2.5">
                {section.links.map((link, linkIndex) => (
                  <FooterLink key={`${section.title}-${link.href}-${linkIndex}`} href={link.href}>
                    {link.label}
                  </FooterLink>
                ))}
              </View>
            </View>
          ))}
        </View>

        <View className="border-brand-brown-dark/10 mt-12 w-full items-center gap-4 border-t pt-6 md:mt-16 md:gap-6 lg:mt-20 lg:flex-row lg:items-center">
          <View className={'flex-1 items-center md:items-start'}>
            <Text className="text-brand-brown text-xs font-semibold md:text-sm">
              © Copyright {new Date().getFullYear()} Get a Pet™
            </Text>
            <Text className="text-brand-brown text-xs md:text-sm">
              Dogs Unleashed AB (559049-6807)
            </Text>
            <Text className="text-brand-brown text-xs md:text-sm">{formatAppVersion()}</Text>
          </View>

          <View className={'flex-row flex-wrap items-center justify-center md:mr-3 md:justify-end'}>
            {legalLinks.map((link, index) => (
              <Fragment key={link.href}>
                {index > 0 && <Text className="text-brand-brown mx-1.5">·</Text>}
                <Link href={link.href} textClassName="text-brand-brown text-xs md:text-sm">
                  {link.label}
                </Link>
              </Fragment>
            ))}
            <Text className="text-brand-brown mx-1.5">·</Text>
            <Pressable accessibilityRole="button" onPress={openCookieSettings}>
              <Text className="text-brand-brown text-xs hover:underline md:text-sm">
                {t('layout.cookie_consent.settings_title')}
              </Text>
            </Pressable>
          </View>

          <LocaleSwitcher />
        </View>
      </View>
    </View>
  );
};

const FooterHeading = ({ children }: Props) => {
  return (
    <Text
      accessibilityRole={'heading'}
      aria-level={2}
      className="text-brand-brown-dark text-base font-black md:text-lg"
    >
      {children}
    </Text>
  );
};

const FooterLink = ({ href, children }: Props<{ href: string }>) => {
  return (
    <View accessibilityRole={'menuitem'} className={'flex-row'}>
      <Link
        href={href}
        containerClassName={'self-start'}
        textClassName="text-brand-brown text-base"
      >
        {children}
      </Link>
    </View>
  );
};

export { Footer };
